<script setup lang="ts">
import { computed, ref } from "vue";
import Modal from "@/components/modals/ModalContainer.vue";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import { auctionApi } from "~/api/auction";
import { Currency } from "fungi-types";
const storeUser = useStoreUserV2();
import { useI18n } from "vue-i18n";

const { locale } = useI18n();

const emit = defineEmits(["onClose"]);

const handleOnClose = () => {
  if (loadingMigrationStatus.value) return;
  emit("onClose");
};

const loadingMigrationStatus = ref(false);
const migrationToUSDCCompleted = ref(false);

const migrateToUSDC = async () => {
  if (loadingMigrationStatus.value) return;
  try {
    loadingMigrationStatus.value = true;
    let status = !storeUser.migrationToUSDCCompleted;
    storeUser.migrationToUSDCCompleted = status;
    if (storeUser.migrationToUSDCCompleted) storeUser.updatePreferredCurrency(Currency.USD);
    await auctionApi.users.switchToUsdc.mutate({ isMigrated: status });

    // waiting 2s
    await new Promise((resolve) => setTimeout(resolve, 2000));

    migrationToUSDCCompleted.value = true;
  } catch (error) {
    console.error("Error toggling wallet currency migration:", error);
  } finally {
    loadingMigrationStatus.value = false;
  }
};

const title = computed(() => {
  if (migrationToUSDCCompleted.value) {
    if (locale.value === "fr") {
      return "Migration vers l’USDC réussie";
    }
    return "Successful Migration to USDC";
  }
  if (locale.value === "fr") {
    return "Fungiball migre vers l’USDC pour une économie plus stable";
  }
  return "Fungiball is migrating to USDC for a more stable economy";
});
</script>

<template>
  <Modal
    :show="true"
    @onClose="handleOnClose"
    :title="title"
    headerIMG="/images/usdc_backround.jpg"
  >
    <template v-if="locale === 'en'">
      <div v-if="migrationToUSDCCompleted" class="flex flex-col gap-2 text-sm">
        <span>
          <span class="text-orange-40">You have successfully migrated your interface to USDC</span>.
        </span>
        <span>Your preferred currency is now set to USD ($).</span>
        <span>
          Don't forget to convert any remaining WPOL to USDC via the Deposit → Swap section to take
          full advantage of the new currency system.
        </span>

        <span>
          You can always
          <span class="text-orange-40">switch back and forth between WPOL and USDC</span>
          in your profile settings until December 28.
        </span>
        <span>
          If you have any questions or need further assistance, please join our Discord. Our team is
          here to help.
        </span>
        <Button type="secondary" size="xs" class="mt-2" @click="handleOnClose">Close</Button>
      </div>
      <div v-else class="flex flex-col gap-2 text-sm">
        <span>
          <span class="text-orange-40">USDC is becoming the new crypto currency</span> on Fungiball,
          bringing more stability and better long-term value for your cards. USDC is a stablecoin
          pegged to the US Dollar, offering a more predictable value compared to WPOL, which can
          fluctuate significantly.
        </span>

        <span>
          <span class="text-orange-40">You can convert your WPOL to USDC</span> directly within the
          game. Simply go to <span class="text-orange-40">Deposit → Swap</span>. Nothing is lost in
          the process.
        </span>

        <span>
          <span class="text-orange-40">You don’t have anything else to do</span>. The transition is
          automatic.
        </span>

        <span
          >We remain on the same network, so
          <span class="text-orange-40">your wallet address does not change</span>.</span
        >

        <span>
          On the primary market,
          <span class="text-orange-40"
            >both WPOL and USDC will be accepted until <b>December 28</b></span
          >.
        </span>
        <span>
          <span class="text-orange-40">All new listings</span> on the secondary market will
          <span class="text-orange-40">use USDC only</span>. To view these listings, you’ll need to
          switch your interface to USD.
        </span>

        <span>
          You will still be able to transfer your WPOL even after the migration period ends.You can
          still switch your interface back to WPOL until December 28.
        </span>

        <span> If you have any questions, join our Discord. Our team is here to help. </span>

        <Button
          @click="migrateToUSDC"
          :loading="loadingMigrationStatus"
          type="primary-blue"
          class="mt-4"
          >Migrate to USDC</Button
        >
        <Button
          :loading="loadingMigrationStatus"
          type="secondary"
          size="xs"
          class="mt-2"
          @click="handleOnClose"
          >Close</Button
        >
      </div>
    </template>
    <template v-else-if="locale === 'fr'">
      <div v-if="migrationToUSDCCompleted" class="flex flex-col gap-2 text-sm">
        <span>
          <span class="text-orange-40">Vous avez migré votre interface vers l’USDC avec succès</span
          >.
        </span>
        <span>Votre devise préférée est maintenant définie sur USD.</span>

        <span>
          N’oubliez pas de convertir vos WPOL restants en USDC via la section
          <span class="text-orange-40">Déposer → Swap</span>
          afin de profiter pleinement du nouveau système de devise.
        </span>

        <span>
          Vous pouvez toujours
          <span class="text-orange-40">basculer entre WPOL et USDC</span>
          dans les paramètres de votre profil jusqu’au 28 décembre.
        </span>

        <span>
          Si vous avez des questions ou besoin d’aide, rejoignez notre Discord. Notre équipe est là
          pour vous accompagner.
        </span>

        <Button type="secondary" size="xs" class="mt-2" @click="handleOnClose">Fermer</Button>
      </div>

      <div v-else class="flex flex-col gap-2 text-sm">
        <span>
          <span class="text-orange-40">L’USDC devient la nouvelle cryptomonnaie</span> sur
          Fungiball, apportant plus de stabilité et une meilleure valeur à long terme pour vos
          cartes. L’USDC est un stablecoin indexé sur le dollar américain, offrant une valeur
          prévisible, contrairement au WPOL qui peut être très volatile.
        </span>

        <span>
          <span class="text-orange-40">Vous pouvez convertir votre WPOL en USDC</span> directement
          depuis le jeu. Il vous suffit d’aller dans
          <span class="text-orange-40">Déposer → Swap</span>.
        </span>

        <span>
          <span class="text-orange-40">Vous n’avez rien d’autre à faire</span>. La transition est
          automatique.
        </span>

        <span>
          Nous restons sur le même réseau, donc
          <span class="text-orange-40">votre adresse de wallet ne change pas</span>. Les dépôts se
          font toujours à la même adresse.
        </span>

        <span>
          Sur le marché primaire,
          <span class="text-orange-40"
            >WPOL et USDC seront acceptés jusqu’au <b>28 décembre</b></span
          >.
        </span>

        <span>
          <span class="text-orange-40">Toutes les nouvelles mises en vente</span> sur le marché
          secondaire <span class="text-orange-40">se feront uniquement en USDC</span>. Pour les
          voir, vous devrez migrer votre interface en USDC.
        </span>

        <span>
          Vous pourrez toujours transférer votre WPOL même après la fin de la période de migration.
          Vous pouvez également repasser votre interface en WPOL jusqu’au 28 décembre.
        </span>

        <span>
          Si vous avez des questions, rejoignez notre Discord. Notre équipe est là pour vous aider.
        </span>

        <Button
          @click="migrateToUSDC"
          :loading="loadingMigrationStatus"
          type="primary-blue"
          class="mt-4"
          >Migrer vers l’USDC</Button
        >

        <Button
          :disabled="loadingMigrationStatus"
          type="secondary"
          size="xs"
          class="mt-2"
          @click="handleOnClose"
          >Fermer</Button
        >
      </div>
    </template>
  </Modal>
</template>
